import { StyleSheet } from "react-native";

import type { UiTheme } from "../theme";

// Styles for the shared SessionSummaryView (read-only session detail). Ported
// from the mobile post-session card's postSessionSummaryStyles, keyed off
// UiTheme so the mobile post-save mount and the web console detail pane share
// one tree. Share-button + offscreen-capture styles stay in the mobile mount
// (native-only); the shared view renders an injected shareSlot instead.
export function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    card: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      padding: s.s4,
      gap: s.s4,
    },
    headlineBlock: {
      gap: s.s1,
    },
    sessionLabel: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.8,
      textTransform: "uppercase",
    },
    headlineLabel: {
      // Neutral by default (a solid/normal session is not a brand moment);
      // earned PR/jump headlines get the gold `pr` token below. Colour budget S3.8.
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontWeight: "800",
      letterSpacing: 1.2,
      textTransform: "uppercase",
      marginTop: s.s1,
    },
    headlineLabelEarned: { color: p.pr },
    headlineRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: s.s3,
      marginTop: s.s1,
    },
    headlineName: {
      color: p.ink,
      fontSize: ty.sizes.md,
      fontWeight: "700",
      flex: 1,
      minWidth: 0,
    },
    headlineValue: {
      color: p.ink,
      fontSize: ty.sizes.lg,
      fontWeight: "800",
      fontVariant: ["tabular-nums"],
    },
    champRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: s.s3,
    },
    champMain: {
      flex: 1,
      minWidth: 0,
    },
    champName: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontWeight: "600",
    },
    champFrom: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: "500",
      fontVariant: ["tabular-nums"],
      marginTop: 2,
    },
    noteRow: { flexDirection: "row", alignItems: "flex-start", gap: s.s3 },
    noteTime: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontVariant: ["tabular-nums"],
    },
    noteText: { flex: 1, color: p.ink, fontSize: ty.sizes.base },
    champValueRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s1,
    },
    champValue: {
      color: p.ink,
      fontSize: ty.sizes.md,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    barsBlock: {
      gap: s.s2,
      paddingTop: s.s2,
      borderTopColor: p.border,
      borderTopWidth: 1,
    },
    sectionLabel: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
      marginBottom: s.s1,
    },
    barRow: {
      gap: s.s1,
    },
    barTextRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    barLabel: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontWeight: "600",
      flex: 1,
      minWidth: 0,
    },
    barValue: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    barTrack: {
      height: 6,
      backgroundColor: p.bg,
      borderRadius: 3,
      overflow: "hidden",
    },
    barFill: {
      height: "100%",
      backgroundColor: p.ink3,
      borderRadius: 3,
    },
    bodyBlock: {
      gap: s.s2,
      paddingTop: s.s2,
      borderTopColor: p.border,
      borderTopWidth: 1,
    },
    bodyWrap: {
      alignItems: "center",
    },
  });
}
